// code-generator.js - Code generation form controller.

import { CONFIG } from './config.js';
import { DB } from './db.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';
import { lazyScreen } from './lazy-screen.js';

// Lazy so the director Dashboard is not pulled into the main bundle via this
// module. Single fire-and-forget call site below.
const DASHBOARD = lazyScreen(() => import('./dashboard.js'), 'DASHBOARD');

export const CODE_GENERATOR = (() => {

  // ── STATE ────────────────────────────────────────────────────────────────────

  let _state = {
    type:       'full',      // 'full' | 'feedback' | 'pillar'
    scope:      'team',      // 'team' | 'season' | 'player'
    teamId:     '',
    playerId:   '',
    expiry:     'season',    // '24h' | '1w' | 'season' | 'custom'
    customDate: '',
    pillars:    [],          // selected pillars when type === 'pillar'
  };

  let _teams   = [];
  let _players = [];

  // ── SHOW ─────────────────────────────────────────────────────────────────────

  async function show(mode = 'team') {
    // Show screen first so DOM elements exist
    UI.showScreen('screen-code-generator');

    // Small yield to let the DOM update before we manipulate it
    await new Promise(r => setTimeout(r, 0));

    // Set title based on mode
    const titleEl = document.getElementById('generator-title');
    const subEl   = document.getElementById('generator-sub');
    if (titleEl) titleEl.textContent = mode === 'season' ? 'Season Block' : 'Generate Code';
    if (subEl)   subEl.textContent   = mode === 'season'
      ? 'One code per team, same window'
      : 'New access code';

    // Reset type to full
    _state.type = 'full';
    document.querySelectorAll('#gen-type-group .form-option').forEach(b => b.classList.remove('form-option--active'));
    document.querySelector('#gen-type-group [data-value="full"]')?.classList.add('form-option--active');
    _syncTypeUI('full');

    // Pre-select scope
    if (mode === 'season') {
      _state.scope = 'season';
      document.querySelectorAll('#gen-scope-group .form-option').forEach(b => b.classList.remove('form-option--active'));
      document.querySelector('#gen-scope-group [data-value="season"]')?.classList.add('form-option--active');
      _syncScopeUI('season');
    } else {
      _state.scope = 'team';
      document.querySelectorAll('#gen-scope-group .form-option').forEach(b => b.classList.remove('form-option--active'));
      document.querySelector('#gen-scope-group [data-value="team"]')?.classList.add('form-option--active');
      _syncScopeUI('team');
    }

    // Reset expiry to season
    document.querySelectorAll('#gen-expiry-group .form-option').forEach(b => b.classList.remove('form-option--active'));
    document.querySelector('#gen-expiry-group [data-value="season"]')?.classList.add('form-option--active');
    _syncExpiryUI('season');

    // Hide result
    _showResult(false);

    // Load teams into select (DOM is ready now)
    await _loadTeams();

    // Set default expiry date to end of season
    const dateInput = document.getElementById('gen-expiry-date');
    if (dateInput) {
      dateInput.min   = UTILS.today();
      dateInput.value = CONFIG.season.endDate;
    }
  }

  // ── TEAM + PLAYER LOADING ────────────────────────────────────────────────────

  async function _loadTeams() {
    if (!_teams.length) {
      try { _teams = await DB.teams.getAll(); } catch { _teams = []; }
    }
    const sel = document.getElementById('gen-team-select');
    if (!sel) return;
    const esc = UTILS.escapeHtml;
    sel.innerHTML = '<option value="">Select a team</option>' +
      _teams.map(t => `<option value="${esc(t.id)}">${esc(t.id)} · ${esc(t.age_group)}</option>`).join('');
    _state.teamId = '';
  }

  async function _loadPlayers(teamId) {
    const sel = document.getElementById('gen-player-select');
    if (!sel) return;
    sel.innerHTML = '<option value="">Loading...</option>';
    try {
      const members = await DB.players.getByTeam(teamId, CONFIG.season.current);
      _players = UTILS.sortPlayersByName(
        members.map(m => m.players).filter(Boolean)
      );
      const esc = UTILS.escapeHtml;
      sel.innerHTML = '<option value="">Select a player</option>' +
        _players.map(p => `<option value="${esc(p.id)}">${esc(p.name)}</option>`).join('');
    } catch {
      sel.innerHTML = '<option value="">Could not load players</option>';
    }
  }

  // ── UI SYNC ──────────────────────────────────────────────────────────────────

  function _syncTypeUI(type) {
    _state.type = type;
    // Pillar section visibility
    const pillarsSection = document.getElementById('gen-pillars-section');
    if (pillarsSection) pillarsSection.style.display = type === 'pillar' ? 'flex' : 'none';
  }

  function _syncScopeUI(scope) {
    _state.scope = scope;
    const teamSection   = document.getElementById('gen-team-section');
    const playerSection = document.getElementById('gen-player-section');
    if (teamSection)   teamSection.style.display   = scope === 'season' ? 'none' : 'flex';
    if (playerSection) playerSection.style.display = scope === 'player' ? 'flex' : 'none';
  }

  function _syncExpiryUI(expiry) {
    _state.expiry = expiry;
    const dateInput = document.getElementById('gen-expiry-date');
    if (dateInput) dateInput.style.display = expiry === 'custom' ? 'block' : 'none';
  }

  function _getExpiryDate() {
    switch (_state.expiry) {
      case '24h':   return UTILS.addDays(UTILS.today(), 1);
      case '1w':    return UTILS.addDays(UTILS.today(), 7);
      case 'season':return CONFIG.season.endDate;
      case 'custom':{
        const val = document.getElementById('gen-expiry-date')?.value;
        return val || CONFIG.season.endDate;
      }
    }
  }

  function _getSelectedPillars() {
    const checkboxes = document.querySelectorAll('#gen-pillars-group input[type="checkbox"]');
    return Array.from(checkboxes).filter(cb => cb.checked).map(cb => cb.value);
  }

  // ── GENERATE ─────────────────────────────────────────────────────────────────

  async function generate() {
    const btn = document.getElementById('gen-submit');
    if (btn) { btn.disabled = true; btn.textContent = 'Generating...'; }

    try {
      const expiresAt = _getExpiryDate();
      const pillars   = _state.type === 'pillar' ? _getSelectedPillars() : null;
      const type      = _state.type === 'pillar' ? 'full' : _state.type;

      // Validate
      if (_state.scope === 'team' && !_state.teamId) {
        UI.toast('Please select a team', 'error');
        return;
      }
      if (_state.scope === 'player' && !_state.playerId) {
        UI.toast('Please select a player', 'error');
        return;
      }
      if (_state.type === 'pillar' && (!pillars || pillars.length === 0)) {
        UI.toast('Please select at least one pillar', 'error');
        return;
      }

      if (_state.scope === 'season') {
        // Generate one code per team
        const teams = _teams.length ? _teams : await DB.teams.getAll();
        const suffix = _state.type === 'feedback' ? 'fb' : null;
        let count = 0;
        for (const team of teams) {
          await DB.codes.create({
            code:       UTILS.generateCode(suffix ? `${team.id}-${suffix}` : team.id),
            team_id:    team.id,
            type,
            expires_at: expiresAt,
            pillars:    pillars ? JSON.stringify(pillars) : null,
            player_id:  null,
          });
          count++;
        }
        _showGeneratedResult(
          null,
          null,
          `${count} codes generated · All teams · Expires ${UTILS.formatDate(expiresAt, 'medium')}`
        );
      } else {
        // Generate single code
        const suffix   = _state.type === 'feedback' ? 'fb'
                       : _state.type === 'pillar'   ? 'pl' : null;
        const teamId   = _state.teamId;
        const code     = UTILS.generateCode(suffix ? `${teamId}-${suffix}` : teamId);
        const playerId = _state.scope === 'player' ? _state.playerId : null;

        await DB.codes.create({
          code,
          team_id:    teamId,
          type,
          expires_at: expiresAt,
          pillars:    pillars ? JSON.stringify(pillars) : null,
          player_id:  playerId,
        });

        _showGeneratedResult(code, UTILS.codeToUrl(code));
      }
    } catch (err) {
      console.error('Code generation failed:', err);
      UI.toast('Failed to generate code', 'error');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = 'Generate code →'; }
    }
  }

  function _showGeneratedResult(code, url, customMessage) {
    _showResult(true);
    const codeEl = document.getElementById('gen-result-code');
    const urlEl  = document.getElementById('gen-result-url');
    const copyBtn= document.getElementById('gen-copy-url');

    if (customMessage) {
      if (codeEl) codeEl.textContent = customMessage;
      if (urlEl)  urlEl.textContent  = 'View all codes in the Codes manager';
      if (copyBtn) copyBtn.style.display = 'none';
    } else {
      if (codeEl) codeEl.textContent = code || '';
      if (urlEl)  urlEl.textContent  = url  || '';
      if (copyBtn) {
        copyBtn.style.display = '';
        copyBtn.onclick = () => {
          navigator.clipboard?.writeText(url)
            .then(() => UI.toast('URL copied', 'success'))
            .catch(() => UI.toast('Copy failed - select the URL manually', 'error'));
        };
      }
    }
  }

  function _showResult(show) {
    const el = document.getElementById('gen-result');
    if (el) el.style.display = show ? 'block' : 'none';
  }

  // ── BIND EVENTS ───────────────────────────────────────────────────────────────

  function bindEvents() {

    // Back
    document.getElementById('back-from-generator')?.addEventListener('click', () => {
      DASHBOARD.showCodeManager();
    });

    // Type options
    document.getElementById('gen-type-group')?.addEventListener('click', e => {
      const btn = e.target.closest('[data-value]');
      if (!btn) return;
      document.querySelectorAll('#gen-type-group .form-option').forEach(b => b.classList.remove('form-option--active'));
      btn.classList.add('form-option--active');
      _syncTypeUI(btn.dataset.value);
    });

    // Scope options
    document.getElementById('gen-scope-group')?.addEventListener('click', e => {
      const btn = e.target.closest('[data-value]');
      if (!btn) return;
      document.querySelectorAll('#gen-scope-group .form-option').forEach(b => b.classList.remove('form-option--active'));
      btn.classList.add('form-option--active');
      _syncScopeUI(btn.dataset.value);
    });

    // Expiry options
    document.getElementById('gen-expiry-group')?.addEventListener('click', e => {
      const btn = e.target.closest('[data-value]');
      if (!btn) return;
      document.querySelectorAll('#gen-expiry-group .form-option').forEach(b => b.classList.remove('form-option--active'));
      btn.classList.add('form-option--active');
      _syncExpiryUI(btn.dataset.value);
    });

    // Team select
    document.getElementById('gen-team-select')?.addEventListener('change', async e => {
      _state.teamId = e.target.value;
      if (_state.scope === 'player' && _state.teamId) {
        await _loadPlayers(_state.teamId);
      }
    });

    // Player select
    document.getElementById('gen-player-select')?.addEventListener('change', e => {
      _state.playerId = e.target.value;
    });

    // Generate
    document.getElementById('gen-submit')?.addEventListener('click', generate);

    // Generate another - reset result AND form state so the next code is built
    // from a clean slate. Without this, switching from e.g. "season block" to
    // "individual player" can leave stale selections from the previous code.
    document.getElementById('gen-another')?.addEventListener('click', () => {
      _showResult(false);
      // Re-run show() with the current "mode" - preserves the title (Season
      // Block vs Generate Code) but rebuilds all the option buttons and selects.
      const titleText = document.getElementById('generator-title')?.textContent || '';
      const mode = /season/i.test(titleText) ? 'season' : 'team';
      show(mode);
    });
  }

  // ── PUBLIC API ────────────────────────────────────────────────────────────────

  return { show, bindEvents };

})();
